import { PaymentBreakdownGroup, PaymentBreakdownLine } from "./types";

export type BreakdownEntry =
  | { type: "caption"; label: string }
  | { type: "line"; line: PaymentBreakdownLine };

/**
 * SUBTOTAL/TOTAL rows always render. A LINE row only renders when it carries
 * a non-zero amount or a description — a zero-amount line with no
 * explanation is just noise (e.g. a fee that didn't apply this time).
 */
export const visibleLines = (
  lines: PaymentBreakdownLine[],
): PaymentBreakdownLine[] =>
  lines.filter((line) => {
    if (line.role !== "LINE") return true;
    const amount = Number(line.amount?.amount ?? 0);
    return amount !== 0 || Boolean(line.description);
  });

/**
 * Inserts a caption entry above the first line of each group, in API order.
 * Groups never reorder lines — they only add a caption before the first
 * matching kind. A kind absent from every group renders ungrouped.
 */
export const withGroupCaptions = (
  lines: PaymentBreakdownLine[],
  groups: PaymentBreakdownGroup[] = [],
): BreakdownEntry[] => {
  const groupLabelByKind = new Map<string, string>();
  groups.forEach((group) => {
    group.kinds.forEach((kind) => {
      if (!groupLabelByKind.has(kind)) {
        groupLabelByKind.set(kind, group.label);
      }
    });
  });

  const seenGroupLabels = new Set<string>();
  const entries: BreakdownEntry[] = [];

  lines.forEach((line) => {
    const groupLabel = groupLabelByKind.get(line.kind);
    if (groupLabel && !seenGroupLabels.has(groupLabel)) {
      seenGroupLabels.add(groupLabel);
      entries.push({ type: "caption", label: groupLabel });
    }
    entries.push({ type: "line", line });
  });

  return entries;
};
